import { useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { DataTableColumn } from 'mantine-datatable';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useCreateTenantApiKey } from './hooks/useCreateTenantApiKey';
import { useRevokeTenantApiKey } from './hooks/useRevokeTenantApiKey';

type TenantApiKey = {
  id: string;
  name: string;
  createdAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

const formatDate = (iso: string | null) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export default function TenantApiKeysTab() {
  return <CompanyWrapper contentComponent={TenantApiKeysContent} />;
}

function TenantApiKeysContent({ company }: { company: Company }) {
  const [createdRawKey, setCreatedRawKey] = useState<string | null>(null);
  const [generateOpened, { open: openGenerate, close: closeGenerate }] = useDisclosure(false);
  const [revealOpened, { open: openReveal, close: closeReveal }] = useDisclosure(false);

  const { createKey, isLoading: isCreating } = useCreateTenantApiKey(company.id);
  const { revokeKey } = useRevokeTenantApiKey(company.id);

  const form = useForm({
    initialValues: { name: '' },
    validate: { name: v => (v.trim() ? null : 'Name is required') },
  });

  const handleGenerate = async (values: { name: string }) => {
    try {
      const result = await createKey({ name: values.name });
      setCreatedRawKey(result.data.rawKey);
      form.reset();
      closeGenerate();
      openReveal();
    } catch (error) {
      notifyAndLogError(
        'Failed to generate API key',
        'An error occurred while generating the key.',
        error,
      );
    }
  };

  const handleRevoke = async (keyId: string) => {
    try {
      await revokeKey(keyId);
    } catch (error) {
      notifyAndLogError(
        'Failed to revoke API key',
        'An error occurred while revoking the key.',
        error,
      );
    }
  };

  const columns: DataTableColumn<TenantApiKey>[] = [
    { accessor: 'name', title: 'Name' },
    { accessor: 'createdAt', title: 'Created', render: row => formatDate(row.createdAt) },
    { accessor: 'lastUsedAt', title: 'Last used', render: row => formatDate(row.lastUsedAt) },
    {
      accessor: 'revokedAt',
      title: 'Status',
      render: row =>
        row.revokedAt ? <Badge color="red">Revoked</Badge> : <Badge color="green">Active</Badge>,
    },
    {
      accessor: 'actions',
      title: 'Actions',
      render: row =>
        row.revokedAt ? null : (
          <Button
            size="xs"
            color="red"
            variant="light"
            onClick={e => {
              e.stopPropagation();
              handleRevoke(row.id);
            }}
          >
            Revoke
          </Button>
        ),
    },
  ];

  return (
    <Box>
      <Modal opened={generateOpened} onClose={closeGenerate} title="Generate API key">
        <form onSubmit={form.onSubmit(handleGenerate)}>
          <Stack>
            <TextInput
              label="Key name"
              placeholder="e.g. Origo integration"
              key={form.key('name')}
              {...form.getInputProps('name')}
            />
            <Group>
              <Button type="submit" loading={isCreating}>
                Generate
              </Button>
              <Button variant="light" onClick={closeGenerate}>
                Cancel
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={revealOpened}
        onClose={closeReveal}
        title="API key generated"
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Stack>
          <Alert color="yellow">Copy this key now — it will not be shown again.</Alert>
          <Text size="sm" fw={500}>
            Your new API key:
          </Text>
          <Text ff="monospace" size="sm" style={{ wordBreak: 'break-all' }}>
            {createdRawKey}
          </Text>
          <Group>
            <CopyButton value={createdRawKey ?? ''}>
              {({ copied, copy }) => (
                <Button variant="light" onClick={copy}>
                  {copied ? 'Copied!' : 'Copy key'}
                </Button>
              )}
            </CopyButton>
            <Button onClick={closeReveal}>Done</Button>
          </Group>
        </Stack>
      </Modal>

      <SimpleTable<TenantApiKey>
        resource={`admin/companies/${company.id}/api-keys`}
        columns={columns}
        title="External API keys"
        showCount
        enableSearch={false}
        pageSize={20}
        primaryButton={{ label: 'Generate key', onClick: openGenerate }}
      />
    </Box>
  );
}
